import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { makeGridPopupContainer } from '../../containers/GridPopup/GridPopup';
import {
    getHintForBannerStatus,
    getTitleForBannerStatus
} from '../../service/BannerHelpers/BannerHelpers';
import { Bem } from '../../service/Bem/Bem';
import { Lang, OperatorModerationInfo, Tld } from '../../typings/common';
import { AdvType, BannerStatus, RejectReasonsData } from '../../typings/domain';
import {
    GdAdPrimaryStatus,
    StatusModerateBannerPage
} from '../../typings/graphql-grid';
import {
    ModerationRejectReasons,
    ModerationRejectReasonsProps
} from '../ModerationRejectReasons/ModerationRejectReasons';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './GridBannerStatusCell.css';
import * as keyset from './GridBannerStatusCell.i18n';

const i18n = i18nFactory(keyset);

type GridBannerStatusCellProps = {
    bid: string;
    bannerType: AdvType;
    status: BannerStatus;
    lang: Lang;
    tld: Tld;
    isOperatorSelfAgency: boolean;
    isOperatorAgency: boolean;
    clientRegion: string;
    rejectReasons?: RejectReasonsData;
    getModerationRejectReasons: (bid: string) => void;
    isTextVisible: boolean;
    operators: OperatorModerationInfo[];
};

const ModerationRejectReasonsPopupContainer = makeGridPopupContainer<
    ModerationRejectReasonsProps
>(ModerationRejectReasons);

const BLOCK = 'grid-banner-status-cell';

const hasModerationStatusHash = {
    [StatusModerateBannerPage.MAYBE]: true,
    [StatusModerateBannerPage.SENT]: true,
    [StatusModerateBannerPage.READY]: true
};

const hasRejectedStatusHash = { [StatusModerateBannerPage.NO]: true };

export class GridBannerStatusCell extends React.PureComponent<
    GridBannerStatusCellProps
> {
    render() {
        const { status, isTextVisible } = this.props;

        if (!status.type) {
            return <div className={Bem.cls(BLOCK, 'status')}>-</div>;
        }

        const title = getTitleForBannerStatus(status.type);

        return (
            <div className={Bem.cls(BLOCK)}>
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'status',
                        mods: {
                            type: Bem.beminize(status.type)
                        }
                    })}
                >
                    {this.getStatusIcon(title)}
                    {isTextVisible && title}
                </div>

                {isTextVisible && this.getHintContent()}
            </div>
        );
    }

    private getStatusIcon(title: string) {
        const { status } = this.props;

        switch (status.type) {
            case GdAdPrimaryStatus.ACTIVE:
                return (
                    <Shape2
                        icon={Shape2Icon.statusRunning}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
            case GdAdPrimaryStatus.MODERATION_REJECTED:
            case GdAdPrimaryStatus.TEMPORARILY_SUSPENDED:
                return (
                    <Shape2
                        icon={Shape2Icon.statusStopped}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
            case GdAdPrimaryStatus.MODERATION:
                return (
                    <Shape2
                        icon={Shape2Icon.statusPending}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
            case GdAdPrimaryStatus.MANUALLY_SUSPENDED:
            case GdAdPrimaryStatus.DRAFT:
            case GdAdPrimaryStatus.ARCHIVED:
            default:
                return (
                    <Shape2
                        icon={Shape2Icon.statusEnded}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={title}
                    />
                );
        }
    }

    private getHintContent = () => {
        const {
            bid,
            bannerType,
            status,
            lang,
            tld,
            rejectReasons,
            clientRegion,
            isOperatorAgency,
            isOperatorSelfAgency,
            operators
        } = this.props;
        const hasInactiveResources =
            status.inactiveResources && status.inactiveResources.length !== 0;
        const hint = getHintForBannerStatus(status);

        const hasOperatorOnModerationStatus = operators.some(
            ({ statusModerate }) => hasModerationStatusHash[statusModerate]
        );

        const hasOperatorRejectedStatus = operators.some(
            ({ statusModerate }) => hasRejectedStatusHash[statusModerate]
        );

        return (
            <>
                {hasOperatorOnModerationStatus && (
                    <div className={Bem.cls(BLOCK, 'hint')}>
                        {i18n('moderation-pending')}
                    </div>
                )}

                <div className={Bem.cls(BLOCK, 'hint')}>
                    {hasOperatorRejectedStatus && (
                        <div
                            className={Bem.cls(BLOCK, 'hint', {
                                inline: !hint
                            })}
                        >
                            {i18n('moderation-rejected')}
                        </div>
                    )}
                    <span className={Bem.cls(BLOCK, 'hint-text')}>
                        {hint}

                        {status.isRejectedOnModeration ||
                        hasInactiveResources ||
                        hasOperatorRejectedStatus ? (
                            <ModerationRejectReasonsPopupContainer
                                cls={Bem.cls(BLOCK, 'hint-info', {
                                    compact: !hint
                                })}
                                bid={bid}
                                bannerType={bannerType}
                                reasons={rejectReasons && rejectReasons.items}
                                contactEmail={
                                    rejectReasons && rejectReasons.contactEmail
                                }
                                clientRegion={clientRegion}
                                isOperatorAgency={isOperatorAgency}
                                isOperatorSelfAgency={isOperatorSelfAgency}
                                lang={lang}
                                tld={tld}
                                error={rejectReasons && rejectReasons.error}
                                onPopupOpen={
                                    this.onModerationRejectReasonsPopupOpen
                                }
                            />
                        ) : null}
                    </span>
                </div>
            </>
        );
    };

    private onModerationRejectReasonsPopupOpen = () => {
        const { rejectReasons, getModerationRejectReasons, bid } = this.props;

        if (rejectReasons === undefined || rejectReasons.error) {
            getModerationRejectReasons(bid);
        }
    };
}
